import {
  normalizeProductExtensions,
  getTranslatedProductTexts,
  getProductPriceInfo,
  getDynamicProductDetails,
  transformEnrichedTaxonomies,
  transformEnrichedAttributes,
  transformBrands,
  transformVariationMappings,
} from "./productDataTransformationHelpers";

import type { RawProduct } from "../../../../../types/index";
import type { TransformedProduct } from "../../types/productApiTypes";
import type { RawImage } from "../../../../../types/api/wcStoreApi/wcStoreApiProductsEndpointTypes";

// Define clear types for the dependencies we will pass in
interface LanguageInfo {
  currentLanguage: string;
  shouldTranslate: boolean;
}

export const transformProduct = (
  product: RawProduct | null | undefined,
  languageInfo: LanguageInfo,
): TransformedProduct | null => {
  if (!product) return null;

  // --- Sanitize and Extract All Extension Data ---
  const {
    relatedProducts,
    enrichedAttributes,
    variationAttributeMappingsData,
    defaultVariation,
    enrichedCategories,
    enrichedTags,
    translations,
    variationGalleryData,
    additionalInfo,
    deliveryEstimation,
    returnAvailability,
    buyXPayY,
  } = normalizeProductExtensions(product.extensions);

  // Dependencies are now passed in, not fetched globally
  const { currentLanguage, shouldTranslate } = languageInfo;

  // --- Run All Transformation Helpers ---
  const {
    name: finalName,
    description: finalDescription,
    shortDescription: finalShortDescription,
  } = getTranslatedProductTexts(
    product,
    translations,
    currentLanguage,
    shouldTranslate,
  );

  // --- Price Logic ---
  // Determine purchasability early
  const isPurchasable = product.is_purchasable ?? false;

  // Pass isPurchasable to handle cases where price is "0" but item is not buyable
  const priceInfo = getProductPriceInfo(
    product,
    defaultVariation,
    isPurchasable,
  );

  // --- Dynamic Details Logic (Stock, SKU, Images, Backorder, Low Stock) ---
  const {
    hasDefaultVariation,
    stockStatus,
    onBackorder,
    mainImage,
    galleryImages,
    sku,
    minStock,
    maxStock,
    lowStockRemaining,
  } = getDynamicProductDetails(product, defaultVariation, variationGalleryData);

  // --- Transform Taxonomies using specific helpers ---
  const transformedBrands = transformBrands(product.brands);

  const transformedEnrichedCategories = transformEnrichedTaxonomies(
    enrichedCategories.items,
    shouldTranslate,
  );
  const transformedEnrichedTags = transformEnrichedTaxonomies(
    enrichedTags.items,
    shouldTranslate,
  );

  // --- Transform Enriched Attributes (including Terms) ---
  const transformedEnrichedAttributes = transformEnrichedAttributes(
    enrichedAttributes.attributes,
    shouldTranslate,
  );

  // --- Transform Variation Attribute Mappings ---
  const variationAttributeMappings = transformVariationMappings(
    variationAttributeMappingsData.items,
    shouldTranslate,
  );

  // --- Build Final Transformed Product Object ---
  const transformedProduct: TransformedProduct = {
    // --- 0. Meta ---
    currencyCode: product.prices.currency_code,

    // --- 1. Core Identification ---
    id: product.id,
    parentId: product.parent || 0,
    name: finalName,
    slug: product.slug,
    permalink: product.permalink,

    // --- 2. Descriptive Content ---
    mainImage: mainImage,
    galleryImages: galleryImages,
    description: finalDescription,
    shortDescription: finalShortDescription,

    // --- 3. Pricing & Sale Information ---
    price: priceInfo.price,
    regularPrice: priceInfo.regularPrice,
    salePrice: priceInfo.salePrice,
    formattedPrice: priceInfo.formattedPrice,
    formattedRegularPrice: priceInfo.formattedRegularPrice,
    formattedSalePrice: priceInfo.formattedSalePrice,
    formattedMinPrice: priceInfo.formattedMinPrice,
    formattedMaxPrice: priceInfo.formattedMaxPrice,
    priceHtml: product.price_html || "",
    onSale: priceInfo.onSale,
    salePercentage: priceInfo.salePercentage,

    // --- 4. Inventory & Stock ---
    stockStatus: stockStatus,
    onBackorder: onBackorder,
    lowStockRemaining: lowStockRemaining,
    minStock: minStock,
    maxStock: maxStock,
    soldIndividually: product.sold_individually,

    // --- 5. Core Properties & Ratings ---
    sku: sku,
    type: product.type,
    groupedProductIds: product.grouped_products || [],
    averageRating: product.average_rating || "0",
    addToCartUrl: product.add_to_cart.url,
    addToCartText: product.add_to_cart.text,
    purchasable: isPurchasable,

    // --- 6. Physical Properties ---
    virtual: additionalInfo.is_virtual ?? false,
    downloadable: additionalInfo.is_downloadable ?? false,
    weight: additionalInfo.weight ?? null,
    dimensions: additionalInfo.dimensions ?? null,
    weightUnit: additionalInfo.weight_unit ?? "kg",
    dimensionUnit: additionalInfo.dimension_unit ?? "cm",

    // --- 7. Taxonomies (Classification) ---
    brands: transformedBrands,
    enrichedCategories: transformedEnrichedCategories,
    enrichedTags: transformedEnrichedTags,

    // --- 8. Variation Details ---
    isVariable: product.type === "variable",
    hasDefaultVariation: hasDefaultVariation,
    defaultVariationData: defaultVariation,
    enrichedAttributes: transformedEnrichedAttributes,
    variationAttributeMappings: variationAttributeMappings,
    variationGallery: variationGalleryData.items || [],

    // --- 9. Related Data ---
    upsellProductIds: relatedProducts.upsell_ids || [],
    crossSellProductIds: relatedProducts.cross_sell_ids || [],

    // --- 10. Delivery & Return Information ---
    deliveryEstimation: deliveryEstimation
      ? {
          minDate: deliveryEstimation.min_date,
          maxDate: deliveryEstimation.max_date,
          minDays: deliveryEstimation.min_days,
          maxDays: deliveryEstimation.max_days,
          displayString: deliveryEstimation.display_string,
        }
      : undefined,

    returnAvailability: returnAvailability
      ? {
          isReturnable: returnAvailability.is_returnable,
          days: returnAvailability.days,
          displayString: returnAvailability.display_string,
        }
      : undefined,

    // --- New Promotion Object ---
    promotion: buyXPayY
      ? {
          isEligible: buyXPayY.is_eligible,
          buyQty: buyXPayY.buy_qty,
          payQty: buyXPayY.pay_qty,
        }
      : undefined,
  };

  return transformedProduct;
};
